import { useClientAssetsStore } from '@/stores/clientAssetsStore';
import { useClientCompaniesStore } from '@/stores/clientCompaniesStore';
import { useClientProjectsStore } from '@/stores/clientProjectsStore';
import { logger } from './loggerService';
import { companyCache } from './companyCache';

/**
 * Service to handle account switching for regular users
 * This ensures clean data state when switching between different company accounts
 */
export function useAccountSwitchingService() {
  
  /**
   * Reset all client stores to ensure clean state
   */
  const resetAllClientStores = async () => {
    logger.info('[AccountSwitching] Resetting all client stores');
    
    try {
      const clientAssetsStore = useClientAssetsStore();
      const clientCompaniesStore = useClientCompaniesStore();
      const clientProjectsStore = useClientProjectsStore();
      
      // Reset each store that has a reset method
      const resetPromises = [];
      
      if (typeof clientAssetsStore.reset === 'function') {
        resetPromises.push(clientAssetsStore.reset());
        logger.debug('[AccountSwitching] Resetting client assets store');
      }
      
      if (typeof clientCompaniesStore.reset === 'function') {
        resetPromises.push(clientCompaniesStore.reset());
        logger.debug('[AccountSwitching] Resetting client companies store');
      }
      
      if (typeof clientProjectsStore.reset === 'function') {
        resetPromises.push(clientProjectsStore.reset());
        logger.debug('[AccountSwitching] Resetting client projects store');
      }
      
      // Wait for all resets to complete
      await Promise.all(resetPromises);
      
      logger.info('[AccountSwitching] All client stores reset successfully');
    } catch (error) {
      logger.error('[AccountSwitching] Failed to reset client stores:', error);
      throw error;
    }
  };

  /**
   * Reset company theme cache to default
   */
  const resetTheme = () => {
    logger.info('[AccountSwitching] Resetting theme to default');
    companyCache.clearTheme();
  };

  /**
   * Handle account switch preparation
   * This should be called when navigating away from an account route
   */
  const prepareAccountSwitch = async () => {
    logger.info('[AccountSwitching] Preparing for account switch');
    
    try {
      // Reset all client stores
      await resetAllClientStores();
      
      // Reset theme
      resetTheme();
      
      logger.info('[AccountSwitching] Account switch preparation complete');
    } catch (error) {
      logger.error('[AccountSwitching] Failed to prepare account switch:', error);
      throw error;
    }
  };

  /**
   * Handle switching from one account directly to another
   * This should be called when navigating from one account route to another
   */
  const switchAccount = async (fromCompanyId, toCompanyId) => {
    if (fromCompanyId === toCompanyId) {
      logger.debug('[AccountSwitching] Switching to same company - no action needed');
      return;
    }
    
    logger.info('[AccountSwitching] Switching accounts directly', {
      from: fromCompanyId,
      to: toCompanyId
    });
    
    try {
      // Prepare the switch (reset stores and theme)
      await prepareAccountSwitch();
      
      logger.info('[AccountSwitching] Direct account switch complete');
    } catch (error) {
      logger.error('[AccountSwitching] Failed to switch accounts:', error);
      throw error;
    }
  };

  /**
   * Check if a route is an account route
   */
  const isAccountRoute = (route) => {
    return route.path.startsWith('/accounts/');
  };

  /**
   * Extract company ID from an account route
   */
  const getCompanyIdFromRoute = (route) => {
    if (isAccountRoute(route)) {
      return route.params.id;
    }
    return null;
  };

  /**
   * Check if we're switching between different company accounts
   */
  const isSwitchingAccounts = (fromRoute, toRoute) => {
    const fromCompanyId = getCompanyIdFromRoute(fromRoute);
    const toCompanyId = getCompanyIdFromRoute(toRoute);
    
    // We're switching accounts if:
    // 1. Both routes are account routes
    // 2. They have different company IDs
    return fromCompanyId && toCompanyId && fromCompanyId !== toCompanyId;
  };

  /**
   * Check if we're leaving an account route for a non-account route
   */
  const isLeavingAccount = (fromRoute, toRoute) => {
    return isAccountRoute(fromRoute) && !isAccountRoute(toRoute);
  };

  return {
    resetAllClientStores,
    resetTheme,
    prepareAccountSwitch,
    switchAccount,
    isAccountRoute,
    getCompanyIdFromRoute,
    isSwitchingAccounts,
    isLeavingAccount
  };
}